/**
 * iOS 호환성을 위한 안전한 날짜 파싱 함수
 */
export const parseDateSafely = (dateStr: string): Date => {
    let date: Date;

    try {
        // ISO 8601 형식인지 확인
        if (dateStr.includes('T')) {
            date = new Date(dateStr);
        } else {
            // ISO 형식이 아닌 경우 안전하게 파싱
            const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
            date = new Date(year, month - 1, day); // month는 0-indexed
        }

        // 유효한 날짜인지 확인
        if (isNaN(date.getTime())) {
            console.warn('Invalid date string:', dateStr);
            throw new Error('Invalid date string');
        }

        return date;
    } catch (error) {
        console.error('Date parsing error:', error);
        throw error;
    }
};

export const formatToKST = (date: string | Date): string => {
    const utcDate = typeof date === 'string' ? parseDateSafely(date) : date;

    // 현재 로컬 시간과 UTC의 차이를 구함 (분 단위 → ms 단위로 변환)
    const localOffset = utcDate.getTimezoneOffset() * 60000;

    // 한국 시간은 UTC+9이므로, UTC 기준에서 +9시간을 더함
    const koreaOffset = 9 * 60 * 60 * 1000;
    const kstDate = new Date(utcDate.getTime() + localOffset + koreaOffset);

    // 날짜 및 시간 요소 추출
    const year = kstDate.getFullYear();
    const month = String(kstDate.getMonth() + 1).padStart(2, '0'); // 1~12월
    const day = String(kstDate.getDate()).padStart(2, '0'); // 01~31일
    const hours = String(kstDate.getHours()).padStart(2, '0'); // 00~23시
    const minutes = String(kstDate.getMinutes()).padStart(2, '0'); // 00~59분
    const seconds = String(kstDate.getSeconds()).padStart(2, '0'); // 00~59초
    const milliseconds = String(kstDate.getMilliseconds()).padStart(3, '0'); // 000~999밀리초

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
};

export function formatToMonthDay(dateStr: string): string {
    // ✅ iOS 호환: T → space, Z 제거
    const safeStr = dateStr.replace('T', ' ').replace('Z', '');

    const date = new Date(safeStr);

    if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateStr);
        return '';
    }

    return `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

// YMD 형식으로 변환 (YYYY-MM-DD)
export const toYMD = (dateStrOrDate: string | Date): string => {
    let date: Date;

    if (typeof dateStrOrDate === 'string') {
        date = parseDateSafely(dateStrOrDate);
    } else {
        date = dateStrOrDate;
    }

    // KST 보정 (UTC+9)
    const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);

    // YYYY-MM-DD 형식 반환
    return kst.toISOString().slice(0, 10);
};

/**
 * 특정 날짜에서 지정된 달수를 뺀 날짜를 반환
 * @param dateStrOrDate - 기준 날짜 (문자열 또는 Date 객체)
 * @param monthsToSubtract - 뺄 달수
 * @returns 새로운 Date 객체
 */
export const subtractMonths = (dateStrOrDate: string | Date, monthsToSubtract: number): Date => {
    let date: Date;

    if (typeof dateStrOrDate === 'string') {
        date = parseDateSafely(dateStrOrDate);
    } else {
        date = new Date(dateStrOrDate);
    }

    const result = new Date(date);
    result.setMonth(result.getMonth() - monthsToSubtract);

    return result;
};

/**
 * 특정 날짜에서 지정된 달수를 뺀 날짜를 YYYY-MM-DD 형식으로 반환
 * @param dateStrOrDate - 기준 날짜 (문자열 또는 Date 객체)
 * @param monthsToSubtract - 뺄 달수
 * @returns YYYY-MM-DD 형식의 문자열
 */
export const subtractMonthsToYMD = (dateStrOrDate: string | Date, monthsToSubtract: number): string => {
    const resultDate = subtractMonths(dateStrOrDate, monthsToSubtract);
    return toYMD(resultDate);
};

/**
 * 특정 날짜에서 지정된 달수를 뺀 날짜를 한국 시간 기준으로 반환
 * @param dateStrOrDate - 기준 날짜 (문자열 또는 Date 객체)
 * @param monthsToSubtract - 뺄 달수
 * @returns 한국 시간 기준 Date 객체
 */
export const subtractMonthsToKST = (dateStrOrDate: string | Date, monthsToSubtract: number): Date => {
    const resultDate = subtractMonths(dateStrOrDate, monthsToSubtract);

    // KST 보정 (UTC+9)
    const kst = new Date(resultDate.getTime() + 9 * 60 * 60 * 1000);

    return kst;
};

/**
 * 특정 날짜에서 지정된 일수를 뺀 날짜를 반환
 * @param dateStrOrDate - 기준 날짜 (문자열 또는 Date 객체)
 * @param daysToSubtract - 뺄 일수
 * @returns 새로운 Date 객체
 */
export const subtractDays = (dateStrOrDate: string | Date, daysToSubtract: number): Date => {
    let date: Date;

    if (typeof dateStrOrDate === 'string') {
        date = parseDateSafely(dateStrOrDate);
    } else {
        date = new Date(dateStrOrDate);
    }

    const result = new Date(date);
    result.setDate(result.getDate() - daysToSubtract);

    return result;
};

/**
 * 날짜를 년월 형식으로 포맷팅 (예: "2024년 3월")
 * @param dateStrOrDate - 날짜 (문자열 또는 Date 객체)
 * @param yearSuffix - 년 접미사 (기본값: "년")
 * @param monthSuffix - 월 접미사 (기본값: "월")
 * @returns 포맷팅된 문자열
 */
export const formatToYearMonth = (
    dateStrOrDate: string | Date,
    yearSuffix: string = '년',
    monthSuffix: string = '월',
): string => {
    let date: Date;

    if (typeof dateStrOrDate === 'string') {
        date = parseDateSafely(dateStrOrDate);
    } else {
        date = new Date(dateStrOrDate);
    }

    const year = date.getFullYear();
    const month = date.getMonth() + 1; // getMonth는 0-indexed

    return `${year}${yearSuffix} ${month}${monthSuffix}`;
};
